import { MessagingSettings } from '@weave/schema-gen-ts/dist/schemas/messaging/settings/v1/service.pb';
import { SettingType_Enum } from '@weave/schema-gen-ts/dist/schemas/messaging/settings/v1/settings.pb';
import { useQuery } from 'react-query';
import { bindHTTP } from '@frontend/schema';

type TruelarkLocationSettingResponse = {
  setting?: {
    truelarkLocationSetting?: {
      businessId?: string;
    };
  };
};

const SchemaMessagingSettingsV1Service = bindHTTP(MessagingSettings);

type UseTruelarkDependencyArgs = {
  locationIds?: string[];
  orgId?: string;
  enabled?: boolean;
};

/**
 * For bridgeline settings, our features depend on having a TrueLark business ID.
 * This hook checks if the user has the necessary scope to access the AI feature settings, and then queries for the TrueLark business ID.
 * If no business ID is found, the features dependent on TrueLark will be disabled.
 */
export const useTruelarkDependency = ({ locationIds, orgId, enabled = true }: UseTruelarkDependencyArgs) => {
  const scopedLocationIds = locationIds?.filter(Boolean) ?? [];
  const hasScope = scopedLocationIds.length > 0 && !!orgId;
  const shouldQuery = enabled && hasScope;

  const messagingSettingsQuery = useQuery({
    queryFn: () =>
      SchemaMessagingSettingsV1Service.List({
        orgId: orgId ?? '',
        locationIds: scopedLocationIds,
        settingType: SettingType_Enum.TRUELARK_LOCATION_SETTING,
      }),
    queryKey: ['tl-location-setting-list', { orgId, locationIds: scopedLocationIds }],
    enabled: shouldQuery,
    retry: 0,
    retryOnMount: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  const businessIds = messagingSettingsQuery.data
    ? (messagingSettingsQuery.data.settings as TruelarkLocationSettingResponse[])
        .map((item) => item.setting?.truelarkLocationSetting?.businessId)
        .filter(Boolean)
    : [];

  const meetsTruelarkDependency = !enabled || (shouldQuery && businessIds.length > 0 && businessIds.every(Boolean));

  return {
    meetsTruelarkDependency,
    isLoading: enabled && messagingSettingsQuery.isLoading,
    isError: enabled && messagingSettingsQuery.isError,
  };
};
